import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useCallback, useEffect, useState } from 'react'
import PromptPreview from '../../components/common/PromptPreview'
import { useExperiments } from '../../hooks/useExperiments'
import { usePrompts } from '../../hooks/usePrompts'
import { usePromptMethods } from '../../hooks/usePromptMethods'
import { useAuth } from '../../providers/AuthProvider'
import { useStandards } from '../../providers/StandardsProvider'
import { KU_TYPES, KnowledgeUnit, PASS_META, PassId, Prompt, PromptMethod, PromptSlot, Run } from '../../types'
import { fetchLCKG, LCKGData } from '../../data/lckg'
import { supabase } from '../../supabaseClient'

const LAST_STD_KEY = 'kosmos_last_std'
// Pilot panel defaults — restored across sessions so the researcher doesn't
// re-pick experiment / standard / condition every time they open Workbench.
const PILOT_EXP_KEY = 'kosmos_pilot_exp_id'
const PILOT_STD_KEY = 'kosmos_pilot_std'
const PILOT_COND_KEY = 'kosmos_pilot_condition_id'
const PILOT_KU_COUNT_KEY = 'kosmos_pilot_ku_count'
const readLS = (k: string) => {
  try {
    return localStorage.getItem(k)
  } catch {
    return null
  }
}
const writeLS = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v)
  } catch {}
}

// ── CDT type badge colors ──────────────────────────────────────────────────────
const KU_TYPE_MAP = Object.fromEntries(KU_TYPES.map((t) => [t.value, t])) as Record<
  string,
  { label: string; color: string }
>
const UNCLEAR_COLOR = '#64748b'

// ── Sub-tab panel ──────────────────────────────────────────────────────────────
type SubPanel = 'editor' | 'versions' | 'runs' | 'pilot'

export default function WorkbenchPage() {
  const { user } = useAuth()
  const {
    prompts,
    getPrompt,
    savePrompt,
    updatePromptInPlace,
    createPrompt,
    deletePromptVersion,
    activateVersion,
    fetchVersionHistory,
    loading,
  } = usePrompts()
  const { methods, createMethod, deleteMethod } = usePromptMethods()
  const [pilotRunning, setPilotRunning] = useState(false)
  const { experiments } = useExperiments(user?.id || '')
  const { findStandard } = useStandards()

  // Pass & slot selection
  const [activePass, setActivePass] = useState<PassId>('pass1')
  const [activeSlot, setActiveSlot] = useState<PromptSlot>('extract')
  const [activeMethodId, setActiveMethodId] = useState<string | null>(null)
  const [subPanel, setSubPanel] = useState<SubPanel>('editor')

  // Add Method dialog
  const [addMethodOpen, setAddMethodOpen] = useState(false)
  const [newMethodName, setNewMethodName] = useState('')
  const [newMethodDescription, setNewMethodDescription] = useState('')
  const [addingMethod, setAddingMethod] = useState(false)
  const [deletingMethod, setDeletingMethod] = useState<PromptMethod | null>(null)

  // AI draft
  const [drafting, setDrafting] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)

  // Editor state
  const [editText, setEditText] = useState<Record<string, string>>({})
  const [editLabel, setEditLabel] = useState<Record<string, string>>({})
  const [showSaveAs, setShowSaveAs] = useState(false)
  const [versionName, setVersionName] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Versions panel
  const [versions, setVersions] = useState<Prompt[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [previewVersion, setPreviewVersion] = useState<Prompt | null>(null)
  const [renamingLabel, setRenamingLabel] = useState<string | null>(null)
  const [renameSaving, setRenameSaving] = useState(false)

  // Runs panel
  const [runs, setRuns] = useState<Run[]>([])
  const [runsLoading, setRunsLoading] = useState(false)
  const [selectedRun, setSelectedRun] = useState<Run | null>(null)

  // LCKG panel
  const [lckg, setLckg] = useState<LCKGData | null>(null)
  const [lckgLoading, setLckgLoading] = useState(false)
  const [lckgStd, setLckgStd] = useState<string | null>(null)
  const [lckgOpen, setLckgOpen] = useState(false)
  const [lastStdId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LAST_STD_KEY)
    } catch {
      return null
    }
  })

  // Pilot run state
  const [pilotExpId, setPilotExpId] = useState<string>(() => readLS(PILOT_EXP_KEY) || '')
  const [pilotStdCode, setPilotStdCode] = useState<string>(() => readLS(PILOT_STD_KEY) || '')
  const [pilotKuCount, setPilotKuCount] = useState<number>(() => {
    const v = Number(readLS(PILOT_KU_COUNT_KEY))
    return Number.isFinite(v) && v >= 1 && v <= 50 ? v : 30
  })
  const [pilotKUs, setPilotKUs] = useState<KnowledgeUnit[] | null>(null)
  const [pilotError, setPilotError] = useState<string | null>(null)
  const [pilotSystemPrompt, setPilotSystemPrompt] = useState<string>('')
  const [pilotSystemDirty, setPilotSystemDirty] = useState(false)
  const [savingPilotSystem, setSavingPilotSystem] = useState(false)
  const [pilotSourceDoc, setPilotSourceDoc] = useState<string | null>(null)
  const [pilotConditionId, setPilotConditionId] = useState<string>(() => readLS(PILOT_COND_KEY) || '')
  const [savingToDataset, setSavingToDataset] = useState(false)
  const [saveDatasetStatus, setSaveDatasetStatus] = useState<string | null>(null)
  const [pilotConditions, setPilotConditions] = useState<{ id: string; name: string }[]>([])
  // Replicate mode: when true, Save to Dataset does NOT wipe prior runs for
  // the same (standard × pass × condition × slot). Used for test-retest
  // reliability — multiple replicates per standard under the same prompt.
  const [keepPriorRuns, setKeepPriorRuns] = useState<boolean>(false)

  // Persist pilot selector values so the next visit defaults to them.
  useEffect(() => {
    writeLS(PILOT_EXP_KEY, pilotExpId)
  }, [pilotExpId])
  useEffect(() => {
    writeLS(PILOT_STD_KEY, pilotStdCode)
  }, [pilotStdCode])
  useEffect(() => {
    writeLS(PILOT_COND_KEY, pilotConditionId)
  }, [pilotConditionId])
  useEffect(() => {
    writeLS(PILOT_KU_COUNT_KEY, String(pilotKuCount))
  }, [pilotKuCount])

  // Load conditions for the selected pilot experiment.
  // Preserve the restored pilotConditionId if it still belongs to the new
  // experiment's condition set; otherwise clear it.
  useEffect(() => {
    if (!pilotExpId) {
      setPilotConditions([])
      setPilotConditionId('')
      return
    }
    supabase
      .from('conditions')
      .select('id, name')
      .eq('experiment_id', pilotExpId)
      .order('sort_order')
      .then(({ data }: { data: { id: string; name: string }[] | null }) => {
        const list = data ?? []
        setPilotConditions(list)
        setPilotConditionId((prev) => (prev && list.some((c) => c.id === prev) ? prev : ''))
      })
  }, [pilotExpId])

  const pass = PASS_META.find((p) => p.id === activePass)!

  // Auto-select a method when the active pass/slot changes, or when methods first load.
  // Prefer the method of the existing active prompt for this slot; fall back to the first method.
  useEffect(() => {
    if (methods.length === 0) return
    // If activeMethodId still valid for the current slot (has a prompt or is a known method), keep it
    if (activeMethodId && methods.some((m) => m.id === activeMethodId)) return
    // Prefer the method of whichever prompt currently exists for this pass+slot
    const existing = prompts.find((p) => p.pass_id === activePass && p.slot === activeSlot && p.method_id)
    setActiveMethodId(existing?.method_id || methods[0].id)
  }, [methods, prompts, activePass, activeSlot, activeMethodId])

  const activeMethod = methods.find((m) => m.id === activeMethodId) || null
  const slotKey = `${activePass}_${activeSlot}_${activeMethodId ?? 'none'}`
  const prompt = activeMethodId ? getPrompt(activePass, activeSlot, activeMethodId) : undefined

  const currentText = editText[slotKey] ?? prompt?.text ?? ''
  const currentLabel = editLabel[slotKey] ?? prompt?.label ?? ''
  const isDirty = prompt ? currentText !== prompt.text : false
  const noPromptYet = !prompt && !!activeMethodId

  // ── Save pilot KUs to the dataset as a real run ─────────────────────
  const handleSaveToDataset = useCallback(async () => {
    if (!user || !pilotExpId || !pilotStdCode || !pilotKUs || pilotKUs.length === 0) return
    setSavingToDataset(true)
    setSaveDatasetStatus(null)
    try {
      // 0. In default mode, find previous runs for this exact (experiment,
      // standard, pass, condition, slot) combination and wipe them before
      // saving the new one. In replicate mode (keepPriorRuns=true), skip
      // the wipe so all replicates accumulate — used for the reliability
      // test (repeat N=5 under the same prompt, measure variance).
      let replacedCount = 0
      if (!keepPriorRuns) {
        const priorRunsQuery = supabase
          .from('runs')
          .select('id')
          .eq('experiment_id', pilotExpId)
          .eq('standard_id', pilotStdCode)
          .eq('pass_id', activePass)
          .eq('prompt_slot', activeSlot)
        const priorRunsQueryFiltered = pilotConditionId
          ? priorRunsQuery.eq('condition_id', pilotConditionId)
          : priorRunsQuery.is('condition_id', null)
        const { data: priorRuns, error: priorErr } = await priorRunsQueryFiltered
        if (priorErr) throw new Error(priorErr.message)
        const priorRunIds = (priorRuns ?? []).map((r: { id: string }) => r.id)
        if (priorRunIds.length > 0) {
          await supabase.from('knowledge_units').delete().in('run_id', priorRunIds)
          await supabase.from('runs').delete().in('id', priorRunIds)
          replacedCount = priorRunIds.length
        }
      }

      // 1. Create a real run record
      const { data: runData, error: runErr } = await supabase
        .from('runs')
        .insert({
          user_id: user.id,
          standard_id: pilotStdCode,
          pass_id: activePass,
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          prompt_slot: activeSlot,
          prompt_label: prompt?.label ?? 'Pilot run',
          prompt_text: currentText,
          source_doc: pilotSourceDoc,
          ku_count_target: pilotKuCount,
          experiment_id: pilotExpId,
          condition_id: pilotConditionId || null,
          status: 'complete',
          system_prompt_snapshot: activeSlot === 'extract' ? pilotSystemPrompt || null : null,
        })
        .select()
        .single()
      if (runErr || !runData) throw new Error(runErr?.message || 'Failed to create run')

      // 2. Insert the pilot KUs as real rows
      const kuInserts = pilotKUs.map((k) => ({
        run_id: runData.id,
        standard_id: pilotStdCode,
        pass_id: activePass,
        name: k.name,
        description: k.description,
        proposed_type: k.proposed_type || 'unclear',
        status: 'draft',
        experiment_id: pilotExpId,
        condition_id: pilotConditionId || null,
      }))
      const { error: kuErr } = await supabase.from('knowledge_units').insert(kuInserts)
      if (kuErr) throw new Error(kuErr.message)

      const replacedSuffix =
        replacedCount > 0 ? ` (replaced ${replacedCount} prior run${replacedCount !== 1 ? 's' : ''})` : ''
      setSaveDatasetStatus(
        `✓ Saved ${pilotKUs.length} KU${pilotKUs.length !== 1 ? 's' : ''} to experiment${replacedSuffix}`,
      )
    } catch (err: unknown) {
      setSaveDatasetStatus(`✗ ${err instanceof Error ? err.message : 'Save failed'}`)
    } finally {
      setSavingToDataset(false)
    }
  }, [
    user,
    pilotExpId,
    pilotConditionId,
    pilotStdCode,
    pilotKUs,
    pilotSourceDoc,
    pilotSystemPrompt,
    pilotKuCount,
    activePass,
    activeSlot,
    currentText,
    prompt,
    keepPriorRuns,
  ])

  // localStorage cache key — preserves unsaved edits across tab close / refresh
  const draftCacheKey = `kosmos_wb_draft_${slotKey}`

  const setCurrentText = (text: string) => {
    setEditText((prev) => ({ ...prev, [slotKey]: text }))
    try {
      localStorage.setItem(
        draftCacheKey,
        JSON.stringify({ text, label: editLabel[slotKey] ?? currentLabel, at: Date.now() }),
      )
    } catch {}
  }
  const setCurrentLabel = (label: string) => {
    setEditLabel((prev) => ({ ...prev, [slotKey]: label }))
    try {
      localStorage.setItem(
        draftCacheKey,
        JSON.stringify({ text: editText[slotKey] ?? currentText, label, at: Date.now() }),
      )
    } catch {}
  }

  // Restore any cached draft on mount or when slot changes
  useEffect(() => {
    try {
      const cached = localStorage.getItem(draftCacheKey)
      if (!cached) return
      const parsed = JSON.parse(cached) as { text: string; label: string; at: number }
      // Only restore if cache is newer than the DB prompt's updated_at (or no prompt exists)
      const dbTime = prompt ? new Date(prompt.created_at).getTime() : 0
      if (parsed.at > dbTime && parsed.text && parsed.text !== prompt?.text) {
        setEditText((prev) => ({ ...prev, [slotKey]: parsed.text }))
        if (parsed.label) setEditLabel((prev) => ({ ...prev, [slotKey]: parsed.label }))
      }
    } catch {}
  }, [draftCacheKey, prompt?.id]) // eslint-disable-line

  // Clear cache when the saved prompt matches the current text (no longer dirty)
  useEffect(() => {
    if (prompt && currentText === prompt.text) {
      try {
        localStorage.removeItem(draftCacheKey)
      } catch {}
    }
  }, [prompt, currentText, draftCacheKey])

  // Load versions when switching to versions panel
  useEffect(() => {
    if (subPanel !== 'versions') return
    if (!activeMethodId) {
      setVersions([])
      return
    }
    setVersionsLoading(true)
    fetchVersionHistory(activePass, activeSlot, activeMethodId).then((data) => {
      setVersions(data)
      setVersionsLoading(false)
    })
  }, [subPanel, activePass, activeSlot, activeMethodId, fetchVersionHistory])

  // Load runs when switching to runs panel
  useEffect(() => {
    if (subPanel !== 'runs') return
    setRunsLoading(true)
    // Fetch all runs for this pass (any standard)
    const loadRuns = async () => {
      const { data } = await (async () => {
        const { supabase } = await import('../../supabaseClient')
        return supabase
          .from('runs')
          .select('*')
          .eq('pass_id', activePass)
          .order('created_at', { ascending: false })
          .limit(30)
      })()
      setRuns((data as Run[]) || [])
      setRunsLoading(false)
    }
    loadRuns()
  }, [subPanel, activePass])

  const handleSaveVersion = async () => {
    if (!prompt) return
    setSaving(true)
    const label = versionName.trim() || currentLabel
    await savePrompt(prompt.id, currentText, label)
    setSaving(false)
    setSaved(true)
    setShowSaveAs(false)
    setVersionName('')
    setTimeout(() => setSaved(false), 2000)
    // Clear local edit since it's now saved
    setEditText((prev) => {
      const next = { ...prev }
      delete next[slotKey]
      return next
    })
    setEditLabel((prev) => {
      const next = { ...prev }
      delete next[slotKey]
      return next
    })
  }

  // In-place update — for iterating on a prompt without creating version
  // noise. Overwrites the current row; no new version row is inserted.
  const handleUpdateInPlace = async () => {
    if (!prompt) return
    setSaving(true)
    await updatePromptInPlace(prompt.id, currentText, currentLabel)
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
    setEditText((prev) => {
      const next = { ...prev }
      delete next[slotKey]
      return next
    })
    setEditLabel((prev) => {
      const next = { ...prev }
      delete next[slotKey]
      return next
    })
  }

  const restoreDefault = () => {
    // Re-read the default from the active prompt
    if (prompt) setCurrentText(prompt.text)
  }

  const loadVersion = (v: Prompt) => {
    setCurrentText(v.text)
    setCurrentLabel(v.label)
    setPreviewVersion(null)
    setSubPanel('editor')
  }

  const refreshVersions = async () => {
    if (!activeMethodId) return
    const data = await fetchVersionHistory(activePass, activeSlot, activeMethodId)
    setVersions(data)
  }

  const handleActivateVersion = async (v: Prompt) => {
    const res = await activateVersion(v.id)
    if (!res.ok) {
      window.alert(res.error || 'Failed to activate version.')
      return
    }
    await refreshVersions()
  }

  useEffect(() => {
    if (!previewVersion) setRenamingLabel(null)
  }, [previewVersion])

  const beginRename = () => {
    if (!previewVersion) return
    setRenamingLabel(previewVersion.label)
  }

  const cancelRename = () => setRenamingLabel(null)

  const commitRename = async () => {
    if (!previewVersion || renamingLabel === null) return
    const next = renamingLabel.trim()
    if (!next || next === previewVersion.label) {
      setRenamingLabel(null)
      return
    }
    setRenameSaving(true)
    await updatePromptInPlace(previewVersion.id, previewVersion.text, next)
    setRenameSaving(false)
    setPreviewVersion({ ...previewVersion, label: next })
    setRenamingLabel(null)
    await refreshVersions()
  }

  const handleDeleteVersion = async (v: Prompt) => {
    const ok = window.confirm(
      `Delete version v${v.version} "${v.label}"?\n\nThis permanently removes this prompt version. The version history chain will be rewired so earlier versions remain reachable.`,
    )
    if (!ok) return
    const res = await deletePromptVersion(v.id)
    if (!res.ok) {
      window.alert(res.error || 'Failed to delete version.')
      return
    }
    if (previewVersion?.id === v.id) setPreviewVersion(null)
    await refreshVersions()
  }

  const insertVar = (v: string) => setCurrentText(currentText + v)

  // ── Method handlers ────────────────────────────────────────────────────
  const openAddMethod = () => {
    setNewMethodName('')
    setNewMethodDescription('')
    setAddMethodOpen(true)
  }

  const saveNewMethod = async () => {
    if (!newMethodName.trim()) return
    setAddingMethod(true)
    const created = await createMethod(newMethodName, newMethodDescription)
    setAddingMethod(false)
    if (created) {
      setActiveMethodId(created.id)
      setAddMethodOpen(false)
    }
  }

  const confirmDeleteMethod = async () => {
    if (!deletingMethod) return
    await deleteMethod(deletingMethod.id)
    setDeletingMethod(null)
    // If we just deleted the active method, switch to the first available
    if (activeMethodId === deletingMethod.id) {
      setActiveMethodId(methods.find((m) => m.id !== deletingMethod.id)?.id || null)
    }
  }

  // ── AI draft a starter prompt for the current pass/slot + method ───────
  const handleDraftPrompt = async () => {
    if (!activeMethod || !user) return
    setDrafting(true)
    setDraftError(null)
    try {
      const passLabel = pass.label
      const slotLabel =
        activeSlot === 'generate'
          ? `${passLabel} source document (${activePass === 'pass2' ? 'lesson plan' : 'domain article'})`
          : `Knowledge Unit (KU) extraction for ${passLabel}`

      // If an experiment context is selected, its Framework & Definitions (or
      // the machine-optimized system prompt) will be sent at run time. The
      // drafted method prompt should NOT redefine KU — it should reference the
      // system prompt's definitions implicitly and focus only on the technique.
      const contextExp = experiments.find((e) => e.id === pilotExpId)
      const contextFramework = contextExp?.framework_definitions || contextExp?.system_prompt || ''

      const metaPrompt = contextFramework
        ? `You are helping a researcher draft a PROMPT TEMPLATE that will be sent to an AI extractor. The AI will also receive a SYSTEM PROMPT that defines the Knowledge Unit (KU) and extraction principles; do NOT redefine those in your template.

Experiment context (this content is ALREADY in the system prompt at run time — reference it, do not repeat it):
---
${contextFramework}
---

Pass: ${passLabel} — ${pass.desc}
Slot: ${slotLabel}
Method to operationalize: "${activeMethod.name}"${activeMethod.description ? ` — ${activeMethod.description}` : ''}

Write a prompt template that ONLY specifies:
- The ${activeMethod.name} technique (how to approach the task, shot count, reasoning style, etc.)
- The specific task for this pass/slot (${slotLabel})
- The input placeholders and expected output format

Do NOT include:
- A KU definition (the system prompt provides it)
- The four extraction principles (atomicity, decomposition, independence, minimal interpretation — already in system prompt)
- Type definitions (fact/concept/procedure/process/principle) — already in system prompt if researcher put them there, otherwise the researcher will add them

Use {STANDARD_ID}, {STANDARD_TEXT}, {SUBJECT}, {GRADE}, {KU_COUNT}${
            activeSlot === 'extract' && activePass !== 'pass1' ? ', {SOURCE_DOC}' : ''
          } as placeholders.

${
  activeSlot === 'extract'
    ? 'Output format: ONLY a JSON array of objects with keys: name, description, proposedType. No preamble, no markdown.'
    : 'Output format: ONLY the source document (no preamble, no markdown).'
}

Return only the prompt template, no commentary.`
        : `You are helping a researcher draft a prompt for Knowledge Unit extraction from curriculum standards. The researcher has not set an experiment context, so this prompt is self-contained.

Context:
- Pass: ${passLabel} — ${pass.desc}
- Slot: ${slotLabel}
- Method to operationalize: "${activeMethod.name}"${activeMethod.description ? ` — ${activeMethod.description}` : ''}

Write a complete prompt template that implements this method for the specified pass and slot. The prompt should:
- Use {STANDARD_ID}, {STANDARD_TEXT}, {SUBJECT}, {GRADE}, {KU_COUNT}${
            activeSlot === 'extract' && activePass !== 'pass1' ? ', {SOURCE_DOC}' : ''
          } as placeholders
- ${
            activeSlot === 'extract'
              ? 'Output ONLY a JSON array of objects with keys: name, description, proposedType (one of fact|concept|procedure|process|principle|unclear). No preamble, no markdown.'
              : 'Output ONLY the source document (no preamble, no markdown).'
          }
- Embody the "${activeMethod.name}" method faithfully
- Stay theory-neutral — do NOT presuppose CDT, CoL, or any specific framework. The researcher will add framework-specific content themselves.

Return only the prompt template, no commentary.`

      const { data, error: invokeErr } = await supabase.functions.invoke('extract-kos', {
        body: {
          slot: 'generate',
          passId: activePass,
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          subject: 'Math',
          promptText: metaPrompt,
          pilot: true,
        },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      const draftText: string = data?.sourceDoc || data?.description || ''
      if (!draftText) throw new Error('Empty draft returned')

      // Populate the editor AND immediately persist as v1 in the DB so the
      // draft survives tab close, navigation, or refresh. Researcher can
      // iterate and save new versions from there.
      const draftLabel = `${activeMethod.name} v1`
      const trimmed = draftText.trim()
      setCurrentText(trimmed)
      setCurrentLabel(draftLabel)

      // If no prompt exists yet for this method+slot, create v1 automatically.
      if (!prompt && activeMethodId) {
        await createPrompt({
          passId: activePass,
          slot: activeSlot,
          methodId: activeMethodId,
          label: draftLabel,
          roleDescription: activeMethod.name,
          text: trimmed,
        })
        // Clear local edit state — the saved prompt will now be the source of truth
        setEditText((prev) => {
          const next = { ...prev }
          delete next[slotKey]
          return next
        })
        setEditLabel((prev) => {
          const next = { ...prev }
          delete next[slotKey]
          return next
        })
      }
    } catch (err) {
      setDraftError(err instanceof Error ? err.message : 'Draft failed')
    } finally {
      setDrafting(false)
    }
  }

  // Save a newly-drafted (previously non-existent) prompt as v1 for this method
  const handleSaveFirstVersion = async () => {
    if (!activeMethodId || !currentText.trim()) return
    setSaving(true)
    await createPrompt({
      passId: activePass,
      slot: activeSlot,
      methodId: activeMethodId,
      label: currentLabel.trim() || `${activeMethod?.name || 'Custom'} v1`,
      roleDescription: activeMethod?.name || 'Custom',
      text: currentText,
    })
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
    // Clear local edit — the saved prompt will now come from the store
    setEditText((prev) => {
      const next = { ...prev }
      delete next[slotKey]
      return next
    })
    setEditLabel((prev) => {
      const next = { ...prev }
      delete next[slotKey]
      return next
    })
  }

  const insertLCExample = (lc: { description: string }, std: { code: string }) => {
    const example = `\n\nExample KU (from LCKG, standard ${std.code}):\n  Name: "${lc.description
      .split(' ')
      .slice(0, 5)
      .join(' ')}"\n  Description: "${lc.description}"`
    setCurrentText(currentText + example)
    setSubPanel('editor')
  }

  const handleFetchLCKG = async (stdCode: string) => {
    setLckgLoading(true)
    setLckgOpen(true)
    setLckgStd(stdCode)
    const data = await fetchLCKG(stdCode)
    setLckg(data)
    setLckgLoading(false)
  }

  // ── Pilot run handler — calls edge function directly, no DB writes ───────────
  // Sync system prompt textarea when experiment changes
  // Prefer the AI-optimized system prompt; fall back to the framework if not yet optimized
  useEffect(() => {
    const exp = experiments.find((e) => e.id === pilotExpId)
    setPilotSystemPrompt(exp?.system_prompt || exp?.framework_definitions || '')
    setPilotSystemDirty(false)
  }, [pilotExpId, experiments])

  const handlePilotRun = useCallback(async () => {
    setPilotKUs(null)
    setPilotError(null)
    // Only reset pilotSourceDoc when running a generate — the extract path
    // needs to reuse the source doc from the previous generate run.
    if (activeSlot === 'generate') setPilotSourceDoc(null)

    if (!pilotStdCode) {
      setPilotError('Select a standard first.')
      return
    }
    const meta = findStandard(pilotStdCode)
    if (!meta) {
      setPilotError('Standard not found in database.')
      return
    }
    const exp = experiments.find((e) => e.id === pilotExpId)

    setPilotRunning(true)
    try {
      const { data, error } = await supabase.functions.invoke('extract-kos', {
        body: {
          pilot: true,
          slot: activeSlot,
          standardId: pilotStdCode,
          standardText: meta.std.text,
          subject: exp?.subject || meta.subject,
          grade: meta.grade,
          passId: activePass,
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          promptText: currentText,
          kuCount: pilotKuCount,
          // For pass2/pass3 extract, pass through the source doc captured
          // from the previous generate pilot run. Otherwise {SOURCE_DOC}
          // interpolates to '' and the LLM sees no article to extract from.
          sourceDoc: activeSlot === 'extract' && activePass !== 'pass1' ? pilotSourceDoc || undefined : undefined,
          // System prompt is the extraction layer — omit on generate runs so
          // the extraction invariants don't warp source-doc production.
          systemPrompt: activeSlot === 'generate' ? undefined : pilotSystemPrompt.trim() || undefined,
        },
      })

      if (error) {
        let msg = error.message
        try {
          const errBody = await (error as unknown as { context?: { text?: () => Promise<string> } }).context?.text?.()
          if (errBody) {
            const parsed = JSON.parse(errBody)
            msg = parsed.error || parsed.message || errBody
          }
        } catch {}
        throw new Error(msg)
      }

      if (activeSlot === 'generate') {
        setPilotSourceDoc(data?.sourceDoc || '(no source doc returned)')
      } else {
        setPilotKUs(data?.kus || [])
      }
    } catch (err: unknown) {
      setPilotError(err instanceof Error ? err.message : 'Run failed')
    } finally {
      setPilotRunning(false)
    }
  }, [
    experiments,
    pilotExpId,
    pilotStdCode,
    pilotKuCount,
    findStandard,
    activePass,
    activeSlot,
    currentText,
    pilotSystemPrompt,
    pilotSourceDoc,
  ])

  const VARS: string[] =
    activeSlot === 'generate'
      ? ['{STANDARD_ID}', '{SUBJECT}', '{GRADE}', '{STANDARD_TEXT}']
      : activePass === 'pass1'
      ? ['{STANDARD_ID}', '{SUBJECT}', '{GRADE}', '{STANDARD_TEXT}', '{KU_COUNT}']
      : ['{STANDARD_ID}', '{SUBJECT}', '{GRADE}', '{STANDARD_TEXT}', '{SOURCE_DOC}', '{KU_COUNT}']

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      {/* ── Page header ── */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.25,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main' }}>
          Prompt Workbench
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Edit and version prompts for each extraction pass
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ── Left panel: extraction pass list ──────────────────── */}
        <Box
          sx={{
            width: 220,
            flexShrink: 0,
            bgcolor: '#f8fafc',
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 1.5, pt: 1.5, pb: 1, flexShrink: 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                fontSize: 10.5,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1,
              }}
            >
              Extraction Pass
            </Typography>

            {[
              {
                pm: PASS_META[0],
                passId: 'pass1' as PassId,
                slots: [
                  { slot: 'extract' as PromptSlot, label: 'Knowledge Units', desc: 'Extract KUs from standard text' },
                ],
              },
              {
                pm: PASS_META[1],
                passId: 'pass2' as PassId,
                slots: [
                  { slot: 'generate' as PromptSlot, label: 'Lesson Plan', desc: 'Generate lesson plan document' },
                  { slot: 'extract' as PromptSlot, label: 'Knowledge Units', desc: 'Distill KUs from lesson plan' },
                ],
              },
              {
                pm: PASS_META[2],
                passId: 'pass3' as PassId,
                slots: [
                  { slot: 'generate' as PromptSlot, label: 'Article', desc: 'Generate domain knowledge article' },
                  { slot: 'extract' as PromptSlot, label: 'Knowledge Units', desc: 'Distill KUs from article' },
                ],
              },
            ].map(({ pm, passId, slots }, gi) => (
              <Box key={passId} sx={{ mb: gi < 2 ? 1.5 : 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: pm.color,
                    fontSize: 10,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.5,
                    px: 0.25,
                  }}
                >
                  {pm.icon} {pm.label}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: `1.5px solid ${pm.color}40`,
                  }}
                >
                  {slots.map((item, i) => {
                    const active = activePass === passId && activeSlot === item.slot
                    return (
                      <Box
                        key={item.slot}
                        onClick={() => {
                          setActivePass(passId)
                          setActiveSlot(item.slot)
                          setSubPanel('editor')
                          setPreviewVersion(null)
                          setSelectedRun(null)
                          setShowSaveAs(false)
                          setVersions([])
                          setRuns([])
                        }}
                        sx={{
                          px: 1.25,
                          py: 0.875,
                          cursor: 'pointer',
                          bgcolor: active ? pm.color : 'background.paper',
                          borderTop: i > 0 ? `1px solid ${pm.color}40` : 'none',
                          transition: 'background-color 0.12s',
                          '&:hover': { bgcolor: active ? pm.color : pm.bg },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, fontSize: 11, display: 'block', color: active ? '#fff' : pm.color }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: 10,
                            display: 'block',
                            color: active ? 'rgba(255,255,255,0.8)' : 'text.disabled',
                          }}
                        >
                          {item.desc}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Right panel: editor / versions / runs ──────────────────── */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Method selector bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fafbfc',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 10.5,
                color: 'text.secondary',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                mr: 0.5,
              }}
            >
              Method
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={activeMethodId || ''}
                onChange={(e) => setActiveMethodId(e.target.value || null)}
                displayEmpty
                sx={{ fontSize: 12, fontWeight: 600, bgcolor: 'background.paper' }}
              >
                {methods.map((m) => (
                  <MenuItem key={m.id} value={m.id} sx={{ fontSize: 12 }}>
                    {m.name}
                    {m.is_default && (
                      <Chip
                        label="default"
                        size="small"
                        sx={{ ml: 0.75, height: 14, fontSize: 8.5, fontWeight: 700 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" onClick={openAddMethod} sx={{ fontSize: 11, fontWeight: 700, minWidth: 0, px: 1.25 }}>
              + Add Method
            </Button>
            {activeMethod && !activeMethod.is_default && (
              <IconButton size="small" onClick={() => setDeletingMethod(activeMethod)} sx={{ color: '#dc2626' }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}

            {/* Experiment Context: drives what system prompt Draft/Pilot inherit */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 10.5,
                  color: 'text.secondary',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
                title="Experiment whose Framework & Definitions should be used when drafting method prompts and piloting. Draft reads it; Pilot sends it at runtime."
              >
                Context
              </Typography>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={pilotExpId}
                  onChange={(e) => setPilotExpId(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: 12, fontWeight: 600, bgcolor: 'background.paper' }}
                >
                  <MenuItem value="" sx={{ fontSize: 12 }}>
                    <em>None (generic drafts)</em>
                  </MenuItem>
                  {experiments.map((e) => {
                    const hasFramework = !!(e.framework_definitions || e.system_prompt)
                    return (
                      <MenuItem key={e.id} value={e.id} sx={{ fontSize: 12 }}>
                        {e.name}
                        {hasFramework && (
                          <Chip
                            label="✓ framework"
                            size="small"
                            sx={{
                              ml: 0.75,
                              height: 14,
                              fontSize: 8.5,
                              fontWeight: 700,
                              bgcolor: '#dcfce7',
                              color: '#166534',
                            }}
                          />
                        )}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            </Box>
          </Box>
          {activeMethod?.description && (
            <Box sx={{ px: 2, py: 0.5, bgcolor: '#fafbfc', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>{activeMethod.description}</Typography>
            </Box>
          )}

          {/* Sub-tabs: Editor / Versions / Runs */}
          <Tabs
            value={subPanel}
            onChange={(_, v) => {
              setSubPanel(v)
              setPreviewVersion(null)
              setSelectedRun(null)
            }}
            sx={{
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              minHeight: 38,
              '& .MuiTab-root': { minHeight: 38, fontSize: 12, fontWeight: 700 },
              '& .MuiTabs-indicator': { bgcolor: pass.color },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Editor{' '}
                  {isDirty && (
                    <Box
                      component="span"
                      sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: pass.color, display: 'inline-block' }}
                    />
                  )}
                </Box>
              }
              value="editor"
              sx={{ color: subPanel === 'editor' ? pass.color : undefined }}
            />
            <Tab
              label={`Versions${versions.length > 0 ? ` (${versions.length})` : ''}`}
              value="versions"
              sx={{ color: subPanel === 'versions' ? pass.color : undefined }}
            />
            <Tab
              label={`Runs${runs.length > 0 ? ` (${runs.length})` : ''}`}
              value="runs"
              sx={{ color: subPanel === 'runs' ? pass.color : undefined }}
            />
            <Tab label="Pilot Run" value="pilot" sx={{ color: subPanel === 'pilot' ? pass.color : undefined }} />
          </Tabs>

          {/* Panel content */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {/* ── EDITOR ── */}
            {subPanel === 'editor' && (
              <Box sx={{ p: 2 }}>
                {/* Empty state banner when no prompt exists for this method */}
                {noPromptYet && (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 2,
                      bgcolor: '#fefce8',
                      border: '1px dashed #eab308',
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Typography sx={{ flex: 1, fontSize: 12, color: '#713f12' }}>
                      No prompt yet for <strong>{activeMethod?.name}</strong> on {pass.label} /{' '}
                      {activeSlot === 'extract' ? 'KU Extraction' : 'Source Material'}. Generate a starter draft or
                      write your own below.
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleDraftPrompt}
                      disabled={drafting}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                        bgcolor: '#a16207',
                        '&:hover': { bgcolor: '#854d0e' },
                      }}
                    >
                      {drafting ? <CircularProgress size={14} color="inherit" /> : '✨ Draft with AI'}
                    </Button>
                  </Box>
                )}

                {draftError && (
                  <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setDraftError(null)}>
                    {draftError}
                  </Alert>
                )}

                {/* Role badge */}
                {prompt?.role_description && (
                  <Box
                    sx={{ mb: 1.5, p: 1.25, bgcolor: pass.bg, borderRadius: 1.5, border: `1px solid ${pass.color}30` }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: pass.color, fontSize: 11, display: 'block', mb: 0.25 }}
                    >
                      🎭 Role: {prompt.role_description}
                    </Typography>
                    <Typography variant="caption" sx={{ color: pass.color, opacity: 0.8, fontSize: 11 }}>
                      {activeSlot === 'generate'
                        ? `Defines how the ${pass.label} source document is generated.`
                        : `Defines how ${pass.label} knowledge is identified and distilled from the source.`}
                    </Typography>
                  </Box>
                )}

                {/* Label + restore */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    value={currentLabel}
                    onChange={(e) => setCurrentLabel(e.target.value)}
                    placeholder="Version label…"
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontWeight: 700,
                        fontSize: 13,
                      },
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={restoreDefault}
                    sx={{
                      flexShrink: 0,
                      minWidth: 0,
                      px: 1.5,
                      fontSize: 14,
                      color: 'text.secondary',
                      borderColor: 'divider',
                    }}
                    title="Restore to saved version"
                  >
                    ↺
                  </Button>
                </Box>

                {/* Variable tokens */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  {VARS.map((v) => (
                    <Box
                      key={v}
                      onClick={() => insertVar(v)}
                      component="span"
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: pass.color,
                        bgcolor: `${pass.color}15`,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: `${pass.color}30` },
                      }}
                    >
                      {v}
                    </Box>
                  ))}
                </Box>

                {/* Textarea editor */}
                <TextField
                  multiline
                  rows={14}
                  value={currentText}
                  onChange={(e) => setCurrentText(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{
                    mb: 1.5,
                    '& textarea': { fontFamily: "'Courier New', monospace", fontSize: 12, lineHeight: 1.65 },
                    '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
                  }}
                />

                {saved && (
                  <Alert severity="success" sx={{ mb: 1.5 }}>
                    Saved as new version
                  </Alert>
                )}

                {noPromptYet ? (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      disabled={!currentText.trim() || saving}
                      onClick={handleSaveFirstVersion}
                      sx={{
                        bgcolor: pass.color,
                        '&:hover': { bgcolor: pass.color + 'cc' },
                        fontSize: 12,
                        fontWeight: 700,
                        px: 2,
                      }}
                    >
                      {saving ? <CircularProgress size={14} color="inherit" /> : 'Save as v1 →'}
                    </Button>
                  </Box>
                ) : !showSaveAs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    {isDirty ? (
                      <Alert severity="warning" sx={{ flex: 1, fontSize: 11, py: 0.25 }}>
                        Unsaved edits — cached locally. <strong>Update</strong> overwrites the current version;{' '}
                        <strong>Save as Version</strong> creates a new one.
                      </Alert>
                    ) : saved ? (
                      <Alert severity="success" sx={{ flex: 1, fontSize: 11, py: 0.25 }}>
                        ✓ Saved
                      </Alert>
                    ) : (
                      <Box sx={{ flex: 1 }} />
                    )}
                    <Button
                      variant="outlined"
                      disabled={!isDirty || saving}
                      onClick={handleUpdateInPlace}
                      title="Overwrite the current prompt in place — no new version created. Use while fine-tuning."
                      sx={{
                        borderColor: pass.color,
                        color: pass.color,
                        fontSize: 12,
                        fontWeight: 700,
                        px: 2,
                        flexShrink: 0,
                        '&:hover': { borderColor: pass.color, bgcolor: pass.color + '11' },
                      }}
                    >
                      {saving ? <CircularProgress size={14} /> : '↻ Update'}
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!isDirty || saving}
                      onClick={() => setShowSaveAs(true)}
                      title="Create a new version row — use to checkpoint the current prompt as a named version."
                      sx={{
                        bgcolor: pass.color,
                        '&:hover': { bgcolor: pass.color + 'cc' },
                        fontSize: 12,
                        fontWeight: 700,
                        px: 2,
                        flexShrink: 0,
                      }}
                    >
                      Save as Version →
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ bgcolor: pass.bg, borderRadius: 1.5, p: 1.5, border: `1px solid ${pass.color}30` }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: pass.color,
                        fontSize: 11,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 0.75,
                      }}
                    >
                      Name this version
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        value={versionName}
                        onChange={(e) => setVersionName(e.target.value)}
                        placeholder={currentLabel}
                        size="small"
                        autoFocus
                        fullWidth
                      />
                      <Button
                        variant="contained"
                        onClick={handleSaveVersion}
                        disabled={saving}
                        sx={{ bgcolor: pass.color, '&:hover': { bgcolor: pass.color + 'cc' }, flexShrink: 0 }}
                      >
                        {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                      </Button>
                      <Button
                        onClick={() => setShowSaveAs(false)}
                        sx={{ flexShrink: 0, minWidth: 0, color: 'text.secondary' }}
                      >
                        ✕
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* LCKG Reference */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: 12 }}>
                        🔗 LCKG Reference
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                        Learning Commons Knowledge Graph · for prompt calibration only
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {lastStdId && (
                        <Box
                          onClick={() => handleFetchLCKG(lastStdId)}
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#065f46',
                            bgcolor: '#ecfdf5',
                            border: '1px solid #065f4640',
                            borderRadius: 1,
                            px: 1.25,
                            py: 0.5,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {lckgLoading ? 'Loading…' : lckgStd === lastStdId && lckg ? 'Refresh' : `Fetch ${lastStdId}`}
                        </Box>
                      )}
                      {lckg && (
                        <Typography
                          onClick={() => setLckgOpen((o) => !o)}
                          variant="caption"
                          sx={{ color: 'text.secondary', cursor: 'pointer' }}
                        >
                          {lckgOpen ? 'Hide' : 'Show'}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {!lastStdId && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 11 }}>
                      Open a standard to fetch LCKG data.
                    </Typography>
                  )}

                  {lckgLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={14} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Fetching LCKG data for {lckgStd}…
                      </Typography>
                    </Box>
                  )}

                  {lckg === null && lckgStd && !lckgLoading && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 11 }}>
                      No LCKG data found for {lckgStd}.
                    </Typography>
                  )}

                  {lckg && lckgOpen && !lckgLoading && (
                    <Box
                      sx={{
                        bgcolor: '#f8fafc',
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Standard */}
                      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#ecfdf5' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: '#065f46',
                            fontSize: 10,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            display: 'block',
                            mb: 0.25,
                          }}
                        >
                          Standard · {lckg.standard.code}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.primary', fontSize: 11.5, lineHeight: 1.5 }}>
                          {lckg.standard.description}
                        </Typography>
                      </Box>

                      {/* Learning Components */}
                      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: 'text.secondary',
                            fontSize: 10,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            display: 'block',
                            mb: 1,
                          }}
                        >
                          LCKG Learning Components ({lckg.learningComponents.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {lckg.learningComponents.map((lc) => (
                            <Box
                              key={lc.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                p: 0.75,
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ flex: 1, fontSize: 11.5, color: 'text.secondary', lineHeight: 1.5 }}
                              >
                                {lc.description}
                              </Typography>
                              <Box
                                onClick={() => insertLCExample(lc, lckg.standard)}
                                sx={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: '#065f46',
                                  bgcolor: '#ecfdf5',
                                  border: '1px solid #065f4640',
                                  borderRadius: 0.75,
                                  px: 0.75,
                                  py: 0.25,
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                }}
                              >
                                + Use
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      {/* Connections by hop */}
                      {([1, 2, 3] as const).map((hop) => {
                        const hopConns = lckg.connections.filter((c) => c.hop === hop)
                        if (!hopConns.length) return null
                        return (
                          <Box
                            key={hop}
                            sx={{ p: 1.5, borderBottom: hop < 3 ? '1px solid' : 'none', borderColor: 'divider' }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: 'text.secondary',
                                fontSize: 10,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                display: 'block',
                                mb: 1,
                              }}
                            >
                              Hop {hop} — {hopConns.length} connected standard{hopConns.length !== 1 ? 's' : ''}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {hopConns.map((conn, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    bgcolor: 'background.paper',
                                    borderRadius: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      px: 1.25,
                                      py: 0.875,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      borderBottom: '1px solid',
                                      borderColor: 'grey.100',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: conn.direction === 'forward' ? '#16a34a' : '#d97706',
                                      }}
                                    >
                                      {conn.direction === 'forward' ? '↑' : '↓'} {conn.type}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 700, color: 'primary.main', fontSize: 11 }}
                                    >
                                      {conn.standard.code}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                                      Grade {conn.standard.gradeLevel?.join(',')}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ px: 1.25, py: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: 11,
                                        display: 'block',
                                        mb: 0.5,
                                        lineHeight: 1.4,
                                      }}
                                    >
                                      {conn.standard.description}
                                    </Typography>
                                    {conn.lcs.map((lc) => (
                                      <Box
                                        key={lc.id}
                                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.5 }}
                                      >
                                        <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>
                                          ·
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{ flex: 1, color: 'text.disabled', fontSize: 10.5, lineHeight: 1.4 }}
                                        >
                                          {lc.description}
                                        </Typography>
                                        <Box
                                          onClick={() => insertLCExample(lc, conn.standard)}
                                          sx={{
                                            fontSize: 9,
                                            fontWeight: 600,
                                            color: '#065f46',
                                            bgcolor: '#ecfdf5',
                                            border: '1px solid #065f4640',
                                            borderRadius: 0.5,
                                            px: 0.5,
                                            py: 0.15,
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                          }}
                                        >
                                          +
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )
                      })}

                      <Box
                        sx={{ px: 1.5, py: 0.75, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}
                      >
                        <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled' }}>
                          Knowledge Graph · Learning Commons · CC BY 4.0 · For research calibration only · Not stored in
                          KOSMOS
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* ── VERSIONS ── */}
            {subPanel === 'versions' && (
              <Box sx={{ p: 2 }}>
                {versionsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : versions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', pt: 6 }}>
                    <Typography sx={{ fontSize: 28, mb: 1 }}>📁</Typography>
                    <Typography variant="body2" color="text.secondary">
                      No saved versions yet.
                    </Typography>
                  </Box>
                ) : previewVersion ? (
                  <Box>
                    <Box
                      onClick={() => setPreviewVersion(null)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        color: 'text.secondary',
                        mb: 1.5,
                        fontSize: 12,
                      }}
                    >
                      ‹ Back
                    </Box>
                    <Card variant="outlined" sx={{ mb: 1.5, borderColor: pass.color + '40', bgcolor: pass.bg }}>
                      <CardContent
                        sx={{
                          py: 1.25,
                          '&:last-child': { pb: 1.25 },
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {renamingLabel !== null ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TextField
                                size="small"
                                value={renamingLabel}
                                autoFocus
                                disabled={renameSaving}
                                onChange={(e) => setRenamingLabel(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') commitRename()
                                  else if (e.key === 'Escape') cancelRename()
                                }}
                                sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 13, fontWeight: 700, py: 0.5 } }}
                              />
                              <IconButton size="small" onClick={commitRename} disabled={renameSaving} title="Save">
                                <CheckIcon sx={{ fontSize: 16, color: pass.color }} />
                              </IconButton>
                              <IconButton size="small" onClick={cancelRename} disabled={renameSaving} title="Cancel">
                                <CloseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: pass.color,
                                  fontSize: 13,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {previewVersion.label}
                              </Typography>
                              <IconButton size="small" onClick={beginRename} title="Rename">
                                <EditIcon sx={{ fontSize: 14, color: pass.color }} />
                              </IconButton>
                            </Box>
                          )}
                          <Typography variant="caption" sx={{ color: pass.color, opacity: 0.8, fontSize: 10 }}>
                            {new Date(previewVersion.created_at).toLocaleString()} · v{previewVersion.version}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => loadVersion(previewVersion)}
                          sx={{ bgcolor: pass.color, '&:hover': { bgcolor: pass.color + 'cc' }, flexShrink: 0, ml: 1 }}
                        >
                          Load into Editor
                        </Button>
                      </CardContent>
                    </Card>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <PromptPreview text={previewVersion.text} color={pass.color} />
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: 'text.secondary',
                        fontSize: 10,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        mb: 0.5,
                      }}
                    >
                      {versions.length} version{versions.length !== 1 ? 's' : ''}
                    </Typography>
                    {versions.map((v) => (
                      <Card key={v.id} variant="outlined">
                        <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: v.is_active ? pass.color : 'grey.400',
                                flexShrink: 0,
                              }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 12,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {v.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                                v{v.version} · {new Date(v.created_at).toLocaleDateString()} · {v.text.length} chars
                                {v.is_active && (
                                  <Chip
                                    label="active"
                                    size="small"
                                    sx={{ ml: 0.5, height: 14, fontSize: 9, bgcolor: pass.bg, color: pass.color }}
                                  />
                                )}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                              <Button
                                size="small"
                                onClick={() => setPreviewVersion(v)}
                                sx={{ fontSize: 11, color: pass.color, bgcolor: pass.bg, minWidth: 0, px: 1 }}
                              >
                                View
                              </Button>
                              <Button
                                size="small"
                                onClick={() => loadVersion(v)}
                                sx={{ fontSize: 11, minWidth: 0, px: 1 }}
                              >
                                Load
                              </Button>
                              {!v.is_active && (
                                <Button
                                  size="small"
                                  onClick={() => handleActivateVersion(v)}
                                  sx={{ fontSize: 11, minWidth: 0, px: 1 }}
                                >
                                  Activate
                                </Button>
                              )}
                              <Button
                                size="small"
                                disabled={v.is_active}
                                onClick={() => handleDeleteVersion(v)}
                                title={
                                  v.is_active
                                    ? 'Activate a different version before deleting this one'
                                    : 'Delete this version'
                                }
                                sx={{
                                  fontSize: 11,
                                  minWidth: 0,
                                  px: 1,
                                  color: v.is_active ? 'text.disabled' : 'error.main',
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 10.5,
                              color: 'text.disabled',
                              fontFamily: 'monospace',
                              display: 'block',
                              mt: 0.75,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              pl: 2.25,
                            }}
                          >
                            {v.text.split('\n')[0]}…
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* ── RUNS ── */}
            {subPanel === 'runs' && (
              <Box sx={{ p: 2 }}>
                {runsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : runs.length === 0 ? (
                  <Box sx={{ textAlign: 'center', pt: 6 }}>
                    <Typography sx={{ fontSize: 28, mb: 1 }}>📊</Typography>
                    <Typography variant="body2" color="text.secondary">
                      No runs yet. Generate KUs for a standard to see runs here.
                    </Typography>
                  </Box>
                ) : selectedRun ? (
                  <Box>
                    <Box
                      onClick={() => setSelectedRun(null)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        color: 'text.secondary',
                        mb: 1.5,
                        fontSize: 12,
                      }}
                    >
                      ‹ Back to runs
                    </Box>
                    <Card variant="outlined" sx={{ mb: 1.5, borderColor: pass.color + '40', bgcolor: pass.bg }}>
                      <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: pass.color, fontSize: 12 }}>
                          {selectedRun.prompt_label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: pass.color, opacity: 0.8, fontSize: 11 }}>
                          {selectedRun.standard_id} · {selectedRun.provider}/{selectedRun.model} ·{' '}
                          {new Date(selectedRun.created_at).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: pass.color,
                            fontSize: 10,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {selectedRun.prompt_slot === 'generate' ? 'Step 1' : 'Step 2'} — {selectedRun.prompt_label}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => {
                            setActiveSlot(selectedRun.prompt_slot as PromptSlot)
                            setCurrentText(selectedRun.prompt_text)
                            setSubPanel('editor')
                          }}
                          sx={{ fontSize: 11, color: pass.color, bgcolor: pass.bg, minWidth: 0, px: 1, py: 0.25 }}
                        >
                          Load
                        </Button>
                      </Box>
                      <PromptPreview text={selectedRun.prompt_text} color={pass.color} />
                    </Box>
                    {selectedRun.latency_ms && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
                        Latency: {selectedRun.latency_ms}ms · Tokens: {selectedRun.input_tokens ?? '—'} in /{' '}
                        {selectedRun.output_tokens ?? '—'} out
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: 'text.secondary',
                        fontSize: 10,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        mb: 0.5,
                      }}
                    >
                      {runs.length} run{runs.length !== 1 ? 's' : ''}
                    </Typography>
                    {runs.map((run) => (
                      <Card
                        key={run.id}
                        variant="outlined"
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                        onClick={() => setSelectedRun(run)}
                      >
                        <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: pass.color, flexShrink: 0 }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 12,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {run.prompt_label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                                {run.standard_id} · {run.provider} · {new Date(run.created_at).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }}>›</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Box>
            )}
            {/* ── PILOT RUN ── */}
            {subPanel === 'pilot' &&
              (() => {
                const pilotExp = experiments.find((e) => e.id === pilotExpId)
                const stdMeta = pilotStdCode ? findStandard(pilotStdCode) : null

                return (
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: 'text.secondary',
                        fontSize: 10,
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 1.5,
                      }}
                    >
                      Pilot Run — {pass.label} · {activeSlot === 'generate' ? 'Generate' : 'Extract'}
                    </Typography>

                    {/* Experiment selector */}
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11, display: 'block', mb: 0.5 }}
                    >
                      Experiment
                    </Typography>
                    {experiments.length === 0 ? (
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 11 }}>
                        No experiments yet. Create one from the Experiments page.
                      </Typography>
                    ) : (
                      <>
                        <Select
                          value={pilotExpId}
                          onChange={(e) => {
                            setPilotExpId(e.target.value)
                            // Only clear std on a *manual* experiment switch when
                            // the saved std doesn't belong to the new experiment.
                            // Left as-is below so the researcher can pick fresh.
                            setPilotStdCode('')
                            setPilotKUs(null)
                            setPilotError(null)
                          }}
                          displayEmpty
                          size="small"
                          fullWidth
                          sx={{ mb: 1, fontSize: 12 }}
                        >
                          <MenuItem value="" disabled>
                            <em>Select an experiment…</em>
                          </MenuItem>
                          {experiments.map((e) => (
                            <MenuItem key={e.id} value={e.id} sx={{ fontSize: 12 }}>
                              {e.name} · {e.subject}
                            </MenuItem>
                          ))}
                        </Select>
                      </>
                    )}

                    {/* ── System Prompt (editable for this pilot run) ── */}
                    <Box sx={{ mt: 1.5, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11 }}>
                          System Prompt (Framework & Definitions)
                          {pilotSystemDirty && (
                            <Chip
                              label="edited"
                              size="small"
                              sx={{
                                ml: 0.75,
                                height: 14,
                                fontSize: 9,
                                fontWeight: 700,
                                bgcolor: '#fef3c7',
                                color: '#92400e',
                              }}
                            />
                          )}
                        </Typography>
                        {pilotSystemDirty && pilotExpId && (
                          <Button
                            size="small"
                            onClick={async () => {
                              const exp = experiments.find((e) => e.id === pilotExpId)
                              if (!exp) return
                              setSavingPilotSystem(true)
                              await supabase
                                .from('experiments')
                                .update({ system_prompt: pilotSystemPrompt.trim() || null })
                                .eq('id', exp.id)
                              setSavingPilotSystem(false)
                              setPilotSystemDirty(false)
                            }}
                            disabled={savingPilotSystem}
                            sx={{ fontSize: 10, fontWeight: 700, minWidth: 0, px: 1 }}
                          >
                            {savingPilotSystem ? <CircularProgress size={10} /> : '↑ Save to Experiment'}
                          </Button>
                        )}
                      </Box>
                      <TextField
                        value={pilotSystemPrompt}
                        onChange={(e) => {
                          setPilotSystemPrompt(e.target.value)
                          setPilotSystemDirty(true)
                        }}
                        placeholder={
                          pilotExpId
                            ? 'Inherited from experiment. Edit to override for this pilot run only, or Save to Experiment to persist.'
                            : 'Optional. Definitions, scope, constraints sent as the system prompt alongside the method prompt.'
                        }
                        multiline
                        rows={4}
                        fullWidth
                        size="small"
                        inputProps={{
                          style: { fontSize: 12, fontFamily: "'Courier New', monospace", lineHeight: 1.55 },
                        }}
                      />
                      {pilotSystemPrompt.trim() && (
                        <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.5 }}>
                          {pilotSystemPrompt.length.toLocaleString()} chars
                          {pilotExpId && !pilotSystemDirty && ' · matches experiment (provider will cache across runs)'}
                        </Typography>
                      )}
                    </Box>

                    {/* ── Custom Prompt preview (what the editor will send) ── */}
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11 }}>
                          Custom Prompt
                          {prompt?.label && (
                            <Chip
                              label={`${activeMethod?.name || 'Method'} · ${prompt.label}`}
                              size="small"
                              sx={{
                                ml: 0.75,
                                height: 14,
                                fontSize: 9,
                                fontWeight: 700,
                                bgcolor: '#e0e7ff',
                                color: '#4338ca',
                              }}
                            />
                          )}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                          {currentText.length ? `${currentText.length.toLocaleString()} chars` : 'empty — edit above'}
                        </Typography>
                      </Box>
                      {currentText.trim() ? (
                        <Box
                          sx={{
                            p: 1,
                            maxHeight: 120,
                            overflowY: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: '#fafafa',
                            fontFamily: "'Courier New', monospace",
                            fontSize: 11,
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                            color: 'text.secondary',
                          }}
                        >
                          {currentText.length > 800 ? currentText.slice(0, 800) + '…' : currentText}
                        </Box>
                      ) : (
                        <Alert severity="warning" sx={{ py: 0.5, fontSize: 11 }}>
                          No custom prompt loaded. Select a Method + Pass above, or use ✦ Draft with AI to start one.
                          Running the pilot with only a system prompt will usually produce nothing useful.
                        </Alert>
                      )}
                    </Box>

                    {/* Standard selector — works with or without experiment */}
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11, display: 'block', mb: 0.5 }}
                    >
                      Standard
                    </Typography>
                    {pilotExp && pilotExp.standards.length > 0 ? (
                      <Select
                        value={pilotStdCode}
                        onChange={(e) => {
                          setPilotStdCode(e.target.value)
                          setPilotKUs(null)
                          setPilotError(null)
                        }}
                        displayEmpty
                        size="small"
                        fullWidth
                        sx={{ mb: 1.5, fontSize: 12 }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select a standard…</em>
                        </MenuItem>
                        {pilotExp.standards.map((code) => {
                          const m = findStandard(code)
                          return (
                            <MenuItem key={code} value={code} sx={{ fontSize: 12 }}>
                              <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{code}</Typography>
                                {m && (
                                  <Typography
                                    sx={{
                                      fontSize: 10.5,
                                      color: 'text.secondary',
                                      whiteSpace: 'normal',
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {m.std.text.slice(0, 100)}
                                    {m.std.text.length > 100 ? '…' : ''}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          )
                        })}
                      </Select>
                    ) : (
                      <TextField
                        value={pilotStdCode}
                        onChange={(e) => {
                          setPilotStdCode(e.target.value.trim())
                          setPilotKUs(null)
                          setPilotError(null)
                        }}
                        placeholder="Paste a CCSS code, e.g. CCSS.MATH.CONTENT.4.OA.A.1"
                        size="small"
                        fullWidth
                        sx={{ mb: 1.5, fontSize: 12 }}
                        inputProps={{ style: { fontFamily: "'Courier New', monospace", fontSize: 12 } }}
                        helperText={
                          pilotStdCode && !findStandard(pilotStdCode)
                            ? 'Standard not found in database'
                            : pilotStdCode
                            ? '✓ Found'
                            : 'No experiment selected — type any CCSS code to test against it.'
                        }
                        FormHelperTextProps={{
                          sx: {
                            fontSize: 10.5,
                            color:
                              pilotStdCode && !findStandard(pilotStdCode)
                                ? 'error.main'
                                : pilotStdCode
                                ? 'success.main'
                                : 'text.disabled',
                          },
                        }}
                      />
                    )}

                    {/* Standard preview */}
                    {stdMeta && (
                      <Box
                        sx={{
                          mb: 1.5,
                          p: 1.25,
                          bgcolor: pass.bg,
                          borderRadius: 1.5,
                          border: `1px solid ${pass.color}30`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: pass.color,
                            fontSize: 10,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            display: 'block',
                            mb: 0.25,
                          }}
                        >
                          {pilotStdCode} · Grade {stdMeta.grade} · {stdMeta.subject}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.primary', fontSize: 11.5, lineHeight: 1.55, display: 'block' }}
                        >
                          {stdMeta.std.text}
                        </Typography>
                        {stdMeta.domain && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled', fontSize: 10, display: 'block', mt: 0.5 }}
                          >
                            {stdMeta.domain}
                            {stdMeta.cluster ? ` › ${stdMeta.cluster}` : ''}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Max KU count (safety ceiling) + Run */}
                    {pilotStdCode && activeSlot === 'extract' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 11, flexShrink: 0 }}
                          title="Ceiling, not a target. The LLM extracts as many as the standard warrants."
                        >
                          Max KUs
                        </Typography>
                        <TextField
                          type="number"
                          value={pilotKuCount}
                          onChange={(e) => setPilotKuCount(Math.max(1, Math.min(50, Number(e.target.value))))}
                          size="small"
                          sx={{ width: 80, '& input': { fontSize: 12, py: 0.75 } }}
                          inputProps={{ min: 1, max: 50 }}
                        />
                      </Box>
                    )}

                    {pilotStdCode && (
                      <Button
                        variant="contained"
                        onClick={handlePilotRun}
                        disabled={pilotRunning}
                        sx={{
                          bgcolor: pass.color,
                          '&:hover': { bgcolor: pass.color + 'cc' },
                          fontSize: 12,
                          fontWeight: 700,
                          mb: 2,
                        }}
                      >
                        {pilotRunning ? (
                          <>
                            <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />
                            Running…
                          </>
                        ) : activeSlot === 'generate' ? (
                          `Generate ${pass.label} document`
                        ) : (
                          `Extract KUs`
                        )}
                      </Button>
                    )}

                    {pilotError && (
                      <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>
                        {pilotError}
                      </Alert>
                    )}

                    {/* Generate result */}
                    {pilotSourceDoc && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: pass.color,
                            fontSize: 10,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            display: 'block',
                            mb: 0.75,
                          }}
                        >
                          Generated Source Document
                        </Typography>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: 'background.paper',
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            maxHeight: 400,
                            overflowY: 'auto',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontSize: 11.5, lineHeight: 1.65, whiteSpace: 'pre-wrap', display: 'block' }}
                          >
                            {pilotSourceDoc}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Extract results */}
                    {pilotKUs && (
                      <Box>
                        {/* Save to dataset row */}
                        {pilotExpId && pilotKUs.length > 0 && activeSlot === 'extract' && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                              p: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: '#f8fafc',
                            }}
                          >
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                              Save as a run:
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <Select
                                value={pilotConditionId}
                                onChange={(e) => setPilotConditionId(e.target.value)}
                                displayEmpty
                                sx={{ fontSize: 11 }}
                              >
                                <MenuItem value="" sx={{ fontSize: 11 }}>
                                  (no condition)
                                </MenuItem>
                                {pilotConditions.map((c) => (
                                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: 11 }}>
                                    {c.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleSaveToDataset}
                              disabled={savingToDataset}
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: '#16a34a',
                                '&:hover': { bgcolor: '#15803d' },
                              }}
                            >
                              {savingToDataset ? (
                                <CircularProgress size={12} sx={{ color: 'white' }} />
                              ) : (
                                '💾 Save to Dataset'
                              )}
                            </Button>
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              title="Keep all prior runs for this combo — for test-retest reliability (replicate mode)"
                            >
                              <input
                                type="checkbox"
                                id="keep-prior-runs"
                                checked={keepPriorRuns}
                                onChange={(e) => setKeepPriorRuns(e.target.checked)}
                                style={{ margin: 0 }}
                              />
                              <Typography
                                component="label"
                                htmlFor="keep-prior-runs"
                                sx={{ fontSize: 11, color: 'text.secondary', cursor: 'pointer', userSelect: 'none' }}
                              >
                                Replicate (keep prior)
                              </Typography>
                            </Box>
                            {saveDatasetStatus && (
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: saveDatasetStatus.startsWith('✓') ? '#166534' : '#b91c1c',
                                  fontWeight: 600,
                                }}
                              >
                                {saveDatasetStatus}
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: pass.color,
                              fontSize: 10,
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                            }}
                          >
                            {pilotKUs.length} Knowledge Unit{pilotKUs.length !== 1 ? 's' : ''} extracted
                          </Typography>
                          {/* CDT type summary */}
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {KU_TYPES.map((t) => {
                              const count = pilotKUs.filter((k) => k.proposed_type === t.value).length
                              if (count === 0) return null
                              return (
                                <Chip
                                  key={t.value}
                                  label={`${t.label} ${count}`}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: t.color + '18',
                                    color: t.color,
                                    border: `1px solid ${t.color}40`,
                                  }}
                                />
                              )
                            })}
                            {pilotKUs.filter((k) => k.proposed_type === 'unclear' || !k.proposed_type).length > 0 && (
                              <Chip
                                label={`Unclear ${
                                  pilotKUs.filter((k) => k.proposed_type === 'unclear' || !k.proposed_type).length
                                }`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  bgcolor: UNCLEAR_COLOR + '18',
                                  color: UNCLEAR_COLOR,
                                  border: `1px solid ${UNCLEAR_COLOR}40`,
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {pilotKUs.map((ku, i) => {
                            const typeMeta = ku.proposed_type ? KU_TYPE_MAP[ku.proposed_type] : null
                            const typeColor = typeMeta?.color ?? UNCLEAR_COLOR
                            const typeLabel = typeMeta?.label ?? 'Unclear'
                            return (
                              <Box
                                key={ku.id || i}
                                sx={{
                                  p: 1.25,
                                  bgcolor: 'background.paper',
                                  borderRadius: 1.5,
                                  border: `1px solid`,
                                  borderColor: 'divider',
                                  borderLeft: `3px solid ${typeColor}`,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                  <Chip
                                    label={typeLabel}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      bgcolor: typeColor + '18',
                                      color: typeColor,
                                      border: `1px solid ${typeColor}40`,
                                      flexShrink: 0,
                                      mt: 0.125,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12.5, lineHeight: 1.4 }}>
                                    {ku.name}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: 11.5,
                                    color: 'text.secondary',
                                    lineHeight: 1.55,
                                    display: 'block',
                                    pl: 3.75,
                                  }}
                                >
                                  {ku.description}
                                </Typography>
                              </Box>
                            )
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )
              })()}
          </Box>
          {/* end right panel content */}
        </Box>
        {/* end right panel */}
      </Box>
      {/* end inner flex */}

      {/* ── Add Method dialog ── */}
      <Dialog open={addMethodOpen} onClose={() => setAddMethodOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Add Method</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Define a custom prompting method. The description guides the AI when drafting starter prompts.
          </Typography>
          <TextField
            label="Method name"
            placeholder="e.g. Socratic"
            value={newMethodName}
            onChange={(e) => setNewMethodName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            label="Description"
            placeholder="What makes this method distinctive? How should it behave?"
            value={newMethodDescription}
            onChange={(e) => setNewMethodDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddMethodOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveNewMethod} disabled={!newMethodName.trim() || addingMethod}>
            {addingMethod ? <CircularProgress size={16} color="inherit" /> : 'Add Method'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Method confirm ── */}
      <Dialog open={!!deletingMethod} onClose={() => setDeletingMethod(null)} maxWidth="xs">
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Delete Method</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            Delete method <strong>{deletingMethod?.name}</strong>? Prompts tagged with this method will keep their text
            but lose the method tag.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeletingMethod(null)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDeleteMethod}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
